import { Navigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useMemo } from 'react';

import { AppRoutePath } from '@ocx-app/pages/AppRoutePath';

import { Typography, LoadingButton, SizingMultiplier } from '@ocx/ui';
import { formatPhoneNumberForInput, redactPhoneNumber } from '@ocx/utils-phone-number';
import { SignupSupportLink } from '@ocx/feat-support';
import { MessageIcon } from '@ocx/ui-icons';
import { useConfiguration } from '@ocx/data-configuration';

import { RequestMagicLinkParams, RequestOtpParams } from '../../signup.types';
import { messages } from '../../signup.messages';
import { ChangeNumberLink } from '../../components';
import { useSignupWaitMessageController } from './use-signup-wait-message-controller.hook';

type SignupWaitMessageControllerProps = {
  phoneNumber: string;
  isRequestMagicLinkLoading: boolean;
  isRequestOTPLoading: boolean;
  isBlockedByTimeout: boolean;
  handleRequestMagicLink(params: RequestMagicLinkParams): Promise<boolean>;
  handleRequestOTP(params: RequestOtpParams): Promise<boolean>;
};

export const SignupWaitMessageController = (props: SignupWaitMessageControllerProps) => {
  const {
    phoneNumber,
    isRequestMagicLinkLoading,
    isRequestOTPLoading,
    isBlockedByTimeout,
    handleRequestMagicLink,
    handleRequestOTP,
  } = props;

  const { handleResendMagicLink, handleLoginViaOTP } = useSignupWaitMessageController({
    phoneNumber,
    isBlockedByTimeout,
    handleRequestMagicLink,
    handleRequestOTP,
  });
  const { enabled: otpEnabled } = useConfiguration('auth.otp');

  const pageTitleValues = useMemo(() => {
    const displayPhoneNumber = formatPhoneNumberForInput(phoneNumber);

    return {
      phoneNumber: displayPhoneNumber,
      phoneNumberRedacted: redactPhoneNumber(displayPhoneNumber),
    };
  }, [phoneNumber]);

  if (!phoneNumber) {
    return <Navigate to={AppRoutePath.AuthWildcard} />;
  }

  return (
    <Stack
      padding={SizingMultiplier.xl}
      spacing={SizingMultiplier.md}
      flex={1}
      alignItems="stretch"
      justifyContent="space-around">
      <Stack spacing={SizingMultiplier.md} useFlexGap>
        <Box textAlign="center">
          <MessageIcon sx={{ width: 120, height: 120 }} />
        </Box>
        <Typography variant="h6" align="center" sx={{ textWrap: 'balance' }}>
          <FormattedMessage {...messages['signup:magic-link-method:page-title']} values={pageTitleValues} />
        </Typography>
        <ChangeNumberLink sx={{ mt: -SizingMultiplier.sm }} />
      </Stack>

      <Stack spacing={SizingMultiplier.md}>
        <Typography color="textSecondary" align="center">
          <FormattedMessage {...messages['signup:didnt-receive-sms-question']} />
        </Typography>
        <LoadingButton
          onClick={handleResendMagicLink}
          disabled={isRequestMagicLinkLoading}
          loading={isRequestMagicLinkLoading}>
          <FormattedMessage {...messages['signup:resend-magic-link:button-text']} />
        </LoadingButton>
        {otpEnabled && (
          <LoadingButton
            variant="outlined"
            onClick={handleLoginViaOTP}
            disabled={isRequestOTPLoading}
            loading={isRequestOTPLoading}>
            <FormattedMessage {...messages['signup:use-otp-method:button-text']} />
          </LoadingButton>
        )}
        <SignupSupportLink sx={{ mt: -SizingMultiplier.md }} />
      </Stack>
    </Stack>
  );
};
